import { Formik } from 'formik'
import { useIntl, FormattedMessage, defineMessages } from 'react-intl'
import { NavLink } from 'react-router-dom'
import styled from 'styled-components'

import { Row } from 'components/v2/Row'
import { Column } from 'components/v2/Column'
import { getUnprocessableErrors } from 'utils/error'
import { Button } from 'components/v2/Button'
import Card from '@10xLabs/react-web/components/Card'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import { CompanyAutocomplete } from 'features/Companies/components/CompanyAutocomplete'
import { SelectLinesByCompany } from 'features/Routes/components/SelectLinesByCompany'
import { newRouteSchema } from './schema'
import {
  GTBusinessClientAutocomplete,
  GTBusinessSiteSuggestion,
  GTCompanyV2VerticalInput,
  GTPlace,
  GTPlaceSuggestion
} from 'gql/types.generated'
import { Label } from 'components/v2/Label'
import { Radio } from 'components/v2/Radio'
import { safeFormatMessage } from '@10xlabs/utils'
import { verticalMessages } from 'features/Companies/components/Vertical/messages'
import { ReactElement, useState } from 'react'
import { Input } from 'components/Input'
import { IRouteDirection } from 'features/Routes/types'
import { isEmpty } from 'lodash'
import FormLayout from 'components/v2/FormLayout'
import Heading from 'components/v2/Heading'
import { RouteMap } from 'features/Routes/components/RouteMap'
import { mapPlacesToRouteStops, routeFormUtils } from 'features/Routes/utils'
import { encodeRouteShape, getTotalDistanceByRoute } from 'utils/google-maps'
import { BusinessClientAutocomplete } from 'features/BusinessClients/components/BusinessClientAutocomplete'
import { BusinessSiteAutocomplete } from 'features/BusinessSites/components/BusinessSiteAutocomplete'
import { HelpOutlineIcon } from 'components/Icons/HelpOutlineIcon'
import Tooltip from 'components/v2/Tooltip'
import { RouteStops } from 'features/Routes/components/RouteStops/RouteStops'
import { DropResult } from 'react-beautiful-dnd'
import { GET_LINES_BY_COMPANY } from 'features/Lines/queries-legacy'
import { useQuery } from '@apollo/client'
import { canSelectCompany, getDefaultCompanyForForm } from 'features/Login/utils'
import { useLogin } from 'features/Login/hooks'

const MEXICO_CITY_LOCATION = { lat: 19.4326, lng: -99.1332 }
const VERTICALS_LIST = [
  GTCompanyV2VerticalInput.Travel,
  GTCompanyV2VerticalInput.City,
  GTCompanyV2VerticalInput.Team,
  GTCompanyV2VerticalInput.Rental
]
const StyledStopsColumn = styled(Column)`
  border: 1px solid #e3e3e3;
  padding: 24px;
  border-radius: 16px;
`
const RadioWrapper = styled.div`
  display: flex;
  gap: 8px 20px;
  justify-content: space-between;
  flex-wrap: wrap;
`

const messages = defineMessages({
  selectVerticalPlaceholder: {
    id: 'routes.new.form.selectVertical.placeholder',
    defaultMessage: 'Select a vertical'
  },
  clientPlaceholder: {
    id: 'businessSites.form.client.placeholder',
    defaultMessage: 'Select a business client'
  },
  sitePlaceholder: {
    id: 'businessSites.form.site.placeholder',
    defaultMessage: 'Select a business site'
  },
  namePlaceholder: {
    id: 'routes.new.form.name.placeholder',
    defaultMessage: 'Enter name'
  },
  destinationPlaceholder: {
    id: 'routes.new.form.destination.placeholder',
    defaultMessage: 'Select destination'
  },
  stopsPlaceholder: {
    id: 'routes.new.form.stops.placeholder',
    defaultMessage: 'Select stops (Optional)'
  },
  companyPlaceholder: {
    id: 'routes.new.form.company.placeholder',
    defaultMessage: 'Select company'
  }
})

const StyledHelpOutlineIcon = styled(HelpOutlineIcon)`
  fill: #916ee8;
  width: 20px;
  height: 20px;
`

const RadioItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  input {
    margin: 0;
  }
`
const RouteStopsContainer = styled.div`
  border: 1px solid transparent;
`
const RouteStopsTitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`
const NewRouteForm = (props: TSFixMe): ReactElement => {
  const { values, setValues, touched, errors, handleBlur, setFieldValue, handleChange } = props
  const intl = useIntl()
  const login = useLogin()

  const { data: linesByCompany, error: loadingCompanyLinesError } = useQuery(GET_LINES_BY_COMPANY, {
    variables: {
      companyId: values.company.id,
      limit: 1000
    },
    skip:
      values.vertical === GTCompanyV2VerticalInput.City ||
      values.vertical === GTCompanyV2VerticalInput.Travel ||
      !values.company.id,
    onCompleted: (data) => {
      setFieldValue('lines', data.companyLines)
    }
  })

  const showCompanyLinesSelector =
    linesByCompany?.companyLines?.length > 1 ||
    values.vertical === GTCompanyV2VerticalInput.City ||
    values.vertical === GTCompanyV2VerticalInput.Travel
  const showClientSelector =
    values.vertical === GTCompanyV2VerticalInput.Team || values.vertical === GTCompanyV2VerticalInput.Rental

  const handleDirectionChange = ({ target: { id: value } }: React.ChangeEvent<HTMLInputElement>): void => {
    const newValues = {
      ...values,
      direction: value
    }

    newValues.encodedShape = ''
    newValues.distance = ''
    newValues.outboundDistance = ''
    newValues.outboundStops = [...values.stops].reverse()

    setValues(newValues)
  }
  const handleVerticalChange = ({ target: { id: value } }: React.ChangeEvent<HTMLInputElement>): void => {
    const newValues = {
      ...values,
      vertical: value,
      encodedShape: '',
      distance: '',
      outboundDistance: '',
      outboundStops: [],
      lines: [],
      stops: [],
      company: getDefaultCompanyForForm(login.accessToken),
      site: { id: '', name: '' },
      client: { id: '', name: '' }
    }

    setValues(newValues)
  }

  const onAddStop = (newStop: GTPlaceSuggestion): void => {
    const newStops = [...values.stops, newStop]
    const newValues = {
      ...values,
      stops: newStops,
      encodedShape: '',
      distance: '',
      outboundDistance: ''
    }
    if (values.direction === IRouteDirection.OUTBOUND) {
      newValues.outboundStops = [...newStops].reverse()
    }
    setValues(newValues)
  }

  const onChangeStop = (place: GTPlaceSuggestion, stopIndex: number): TSFixMe => {
    const newStops = [...values.stops]
    newStops[stopIndex] = place
    const newValues = {
      ...values,
      stops: newStops,
      encodedShape: '',
      distance: '',
      outboundDistance: ''
    }
    if (values.direction === IRouteDirection.OUTBOUND) {
      newValues.outboundStops = [...newStops].reverse()
    }
    setValues(newValues)
  }
  const onDelete = (stopIndex: number): void => {
    const newStops = values.stops.filter((stop: GTPlace, index: number) => {
      return index !== stopIndex
    })
    const newValues = {
      ...values,
      encodedShape: '',
      encodedOutboundShape: '',
      distance: '',
      outboundDistance: '',
      stops: newStops
    }

    if (values.direction === IRouteDirection.OUTBOUND) {
      newValues.outboundStops = [...newStops].reverse()
    }

    setValues(newValues)
  }

  const handleDragEnd = (result: DropResult): void => {
    if (!result.destination) {
      return
    }
    const newStops = [...values.stops]
    const stopsReordered = newStops
    const [reorderedItem] = stopsReordered.splice(result.source.index, 1)
    stopsReordered.splice(result.destination.index, 0, reorderedItem)

    setValues({
      ...values,
      stops: stopsReordered,
      encodedShape: '',
      distance: ''
    })
  }

  const handleCompanyChange = (value: TSFixMe): void => {
    setValues({
      ...values,
      company: value,
      client: { id: '', name: '' },
      site: { id: '', name: '' },
      stops: [],
      encodedShape: '',
      distance: '',
      outboundDistance: ''
    })
  }
  const isDisabledAddStops =
    (values.vertical === GTCompanyV2VerticalInput.Team || values.vertical === GTCompanyV2VerticalInput.Rental) &&
    (isEmpty(values.company.id) || isEmpty(values.client.id))

  const shouldFilterStopsByCompany =
    values.vertical === GTCompanyV2VerticalInput.Team || values.vertical === GTCompanyV2VerticalInput.Rental

  return (
    <FormLayout.Card small>
      <Row>
        <Column>
          <Row>
            <Column>
              <Label>
                <FormattedMessage id="routes.new.form.vertical.label" defaultMessage="Vertical" />
              </Label>
              <RadioWrapper>
                {VERTICALS_LIST.map((vertical: GTCompanyV2VerticalInput): ReactElement => {
                  return (
                    <Radio
                      id={vertical}
                      key={vertical}
                      name="vertical"
                      checked={values.vertical === vertical}
                      onChange={handleVerticalChange}
                      disabled={props.isEdit}
                    >
                      {safeFormatMessage({
                        intl,
                        key: vertical,
                        messages: verticalMessages
                      })}
                    </Radio>
                  )
                })}
              </RadioWrapper>
              {touched.vertical && errors.vertical && <ErrorMessage>{errors.vertical}</ErrorMessage>}
            </Column>
          </Row>

          {canSelectCompany(login.accessToken) && (
            <Row>
              <Column>
                <CompanyAutocomplete
                  id="company.id"
                  onChange={handleCompanyChange}
                  onBlur={handleBlur}
                  value={values.company.name}
                  placeholder={intl.formatMessage(messages.companyPlaceholder)}
                  disabled={isEmpty(values.vertical)}
                  touched={touched.company && touched.company.id}
                  errors={errors.company && errors.company.id}
                  verticals={[values.vertical]}
                  label={<FormattedMessage id="dictionary.company" defaultMessage="Company" />}
                />
                {loadingCompanyLinesError && (
                  <ErrorMessage>
                    <FormattedMessage
                      id="routes.new.form.error.loadingCompanyLines"
                      defaultMessage="There was an error while loading the company lines"
                    />
                  </ErrorMessage>
                )}
              </Column>
            </Row>
          )}
          {showClientSelector ? (
            <>
              <Row>
                <Column>
                  <BusinessClientAutocomplete
                    id="client"
                    companyId={values.company.id}
                    value={values.client.name}
                    onChange={(businessClient: GTBusinessClientAutocomplete): void => {
                      setValues({
                        ...values,
                        client: businessClient,
                        site: { id: '', name: '' },
                        stops: [],
                        encodedShape: '',
                        distance: ''
                      })
                    }}
                    placeholder={intl.formatMessage(messages.clientPlaceholder)}
                    label={<FormattedMessage id="businessSites.form.client.label" defaultMessage="Business client" />}
                    touched={touched.client?.id}
                    errors={errors.client?.id}
                    disabled={isEmpty(values.company.id)}
                  />
                </Column>
              </Row>
              <Row>
                <Column>
                  <BusinessSiteAutocomplete
                    id="site"
                    value={values.site.name}
                    onChange={(businessSite: GTBusinessSiteSuggestion): void => {
                      setValues({
                        ...values,
                        site: businessSite,
                        stops: [],
                        encodedShape: '',
                        distance: ''
                      })
                    }}
                    placeholder={intl.formatMessage(messages.sitePlaceholder)}
                    label={<FormattedMessage id="businessSites.form.site.label" defaultMessage="Business site" />}
                    touched={touched.site?.id}
                    errors={errors.site?.id}
                    disabled={isEmpty(values.client.id)}
                    businessClientId={values.client.id}
                  />
                </Column>
              </Row>
            </>
          ) : null}

          {showCompanyLinesSelector ? (
            <Row>
              <Column>
                <label>
                  <Label>
                    <FormattedMessage id="dictionary.lines" defaultMessage="Lines" />
                  </Label>

                  <SelectLinesByCompany
                    values={values.lines}
                    companyId={values.company.id}
                    onChange={(values: TSFixMe): TSFixMe => {
                      setFieldValue('lines', values)
                    }}
                  />

                  {touched.lines && errors.lines && <ErrorMessage>{errors.lines}</ErrorMessage>}
                </label>
              </Column>
            </Row>
          ) : null}

          <Row>
            <Column>
              <Label>
                <FormattedMessage id="routes.new.form.direction.label" defaultMessage="Direction" />
              </Label>
              <RadioWrapper>
                <Column width="50%">
                  <RadioItem>
                    <Radio
                      id={IRouteDirection.INBOUND}
                      name="direction"
                      checked={values.direction === IRouteDirection.INBOUND}
                      onChange={handleDirectionChange}
                      disabled={props.isEdit}
                    >
                      <FormattedMessage id="routes.new.form.direction.inbound.label" defaultMessage="Inbound" />
                    </Radio>
                    <Tooltip
                      content={
                        <FormattedMessage
                          id="routes.new.form.direction.inbound.label.tooltip.description"
                          defaultMessage="If this option is selected, an inbound route is created."
                        />
                      }
                      position="right"
                    >
                      <StyledHelpOutlineIcon />
                    </Tooltip>
                  </RadioItem>
                </Column>
                <Column width="50%">
                  <RadioItem>
                    <Radio
                      id={IRouteDirection.OUTBOUND}
                      name="direction"
                      checked={values.direction === IRouteDirection.OUTBOUND}
                      onChange={handleDirectionChange}
                      disabled={props.isEdit}
                    >
                      <FormattedMessage id="routes.new.form.direction.outbound.label" defaultMessage="Outbound" />
                    </Radio>
                    <Tooltip
                      content={
                        <FormattedMessage
                          id="routes.new.form.direction.outbound.label.tooltip.description"
                          defaultMessage="If this option is selected, an inbound and outbound route are created."
                        />
                      }
                      position="right"
                    >
                      <StyledHelpOutlineIcon />
                    </Tooltip>
                  </RadioItem>
                </Column>
              </RadioWrapper>
              {touched.vertical && errors.vertical && <ErrorMessage>{errors.vertical}</ErrorMessage>}
            </Column>
          </Row>
          <Row>
            <Column width="50%">
              <Input
                id="inboundRouteName"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.inboundRouteName}
                placeholder={intl.formatMessage(messages.namePlaceholder)}
                touched={touched.inboundRouteName}
                errors={errors.inboundRouteName}
                label={<FormattedMessage id="routes.new.form.name.label" defaultMessage="Inbound Route Name" />}
              />
            </Column>
            <Column width="50%">
              <Input
                id="outboundRouteName"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.outboundRouteName}
                placeholder={intl.formatMessage(messages.namePlaceholder)}
                touched={touched.outboundRouteName}
                errors={errors.outboundRouteName}
                label={<FormattedMessage id="routes.new.form.shortName.label" defaultMessage="Outbound Route Name" />}
                disabled={values.direction === IRouteDirection.INBOUND}
              />
            </Column>
          </Row>
        </Column>
        <StyledStopsColumn>
          <Row>
            <Column>
              <RouteStopsTitleContainer>
                <Heading.H4 small>
                  <FormattedMessage id="routes.new.form.direction.label" defaultMessage="Stops" />
                </Heading.H4>
              </RouteStopsTitleContainer>
              <RouteStopsContainer>
                <RouteStops
                  showLabels={false}
                  stops={values.stops}
                  onChange={onChangeStop}
                  onDelete={onDelete}
                  handleBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                  handleDragEnd={handleDragEnd}
                  companyId={shouldFilterStopsByCompany ? values.company.id : undefined}
                  businessSiteId={values.site.id}
                  businessClientId={values.client.id}
                  onAddStop={onAddStop}
                  showAddStop={!isDisabledAddStops}
                  vertical={values.vertical}
                />
              </RouteStopsContainer>
              {values.distance ? (
                <p>
                  <FormattedMessage
                    id="routes.new.form.destination.label"
                    defaultMessage="<strong>Kilometers of travel on the route</strong>: <em>{distance} km</em>"
                    values={{
                      distance: routeFormUtils.getDistanceLabel(values),
                      strong: (msg) => <strong>{msg}</strong>,
                      em: (msg) => <em>{msg}</em>
                    }}
                  />
                </p>
              ) : null}
              {props.errors.encodedShape && props.touched.encodedShape && (
                <div>
                  <ErrorMessage>{props.errors.encodedShape}</ErrorMessage>
                </div>
              )}
            </Column>
          </Row>
        </StyledStopsColumn>
      </Row>
    </FormLayout.Card>
  )
}

export const NewRoute = ({ route = {}, onSubmit, error, loading }: TSFixMe): ReactElement => {
  const intl = useIntl()
  const login = useLogin()

  const [calculateShapeError, setCalculateShapeError] = useState<boolean>(false)
  const initialValues = {
    vertical: route.vertical || 'TRAVEL',
    inboundRouteName: route.inboundRouteName || '',
    outboundRouteName: route.outboundRouteName || '',
    client: route.client || { id: '', name: '' },
    direction: route.direction || IRouteDirection.INBOUND,
    site: route.site || { id: '', name: '' },
    stops: route.stops || [],
    outboundStops: route.outboundStops || [],
    origin: route.origin || { name: '' },
    destination: route.destination || { name: '' },
    company: route.company || getDefaultCompanyForForm(login.accessToken),
    lines: [],
    mapCenterLocation: route.mapCenterLocation || MEXICO_CITY_LOCATION,
    encodedShape: '',
    encodedOutboundShape: '',
    distance: '',
    outboundDistance: ''
  }

  return (
    <Formik initialValues={initialValues} validationSchema={newRouteSchema(intl)} onSubmit={onSubmit}>
      {(props): ReactElement => {
        const onCalculateShape = (
          inboundDirectionsRoute: google.maps.DirectionsRoute,
          outboundDirectionsRoute?: google.maps.DirectionsRoute
        ): void => {
          const distance = getTotalDistanceByRoute(inboundDirectionsRoute)
          const encodedShape = encodeRouteShape(inboundDirectionsRoute)
          const newValues = {
            ...props.values,
            encodedShape,
            distance: String(distance)
          }

          if (props.values.direction === IRouteDirection.OUTBOUND && outboundDirectionsRoute) {
            const outboundDistance = getTotalDistanceByRoute(outboundDirectionsRoute)
            const encodedOutboundShape = encodeRouteShape(outboundDirectionsRoute)

            newValues.encodedOutboundShape = encodedOutboundShape
            newValues.outboundDistance = String(outboundDistance)
          }

          props.setValues(newValues)
        }

        const onCalculateShapeError = (error: TSFixMe): void => {
          setCalculateShapeError(error)
        }

        return (
          <FormLayout.Content as="form" onSubmit={props.handleSubmit} isLargeContent>
            <Heading.H3 capitalize>
              <FormattedMessage id="routes.new.title" defaultMessage="Create route" />
            </Heading.H3>
            <Row noMargin>
              <Column width="70%">
                <NewRouteForm {...props} error={error} loading={loading} />
              </Column>
              <Column>
                <RouteMap
                  onCalculateShapeError={onCalculateShapeError}
                  onCalculateShape={onCalculateShape}
                  encodedShapePath={props.values.encodedShape}
                  mapCenterLocation={props.values.mapCenterLocation}
                  stops={mapPlacesToRouteStops(props.values.stops)}
                  outboundStops={mapPlacesToRouteStops(props.values.outboundStops)}
                />
              </Column>
            </Row>
            {error && (
              <div>
                <ErrorMessage errors={getUnprocessableErrors(error)}>
                  <FormattedMessage
                    id="routes.new.form.error"
                    defaultMessage="There was an error while creating your route."
                  />
                </ErrorMessage>
              </div>
            )}
            {calculateShapeError && (
              <div>
                <ErrorMessage>
                  <FormattedMessage
                    id="routes.new.form.error.shape"
                    defaultMessage="There was an error while calculating your shape."
                  />
                </ErrorMessage>
              </div>
            )}
            <FormLayout.Actions>
              <Card.FooterActions>
                <Button as={NavLink} to="/routes" kind="cancel">
                  <FormattedMessage id="routes.new.form.cancel" defaultMessage="Cancel" />
                </Button>

                <Button type="submit" kind="primary" loading={loading}>
                  <FormattedMessage id="routes.new.form.submit" defaultMessage="Create" />
                </Button>
              </Card.FooterActions>
            </FormLayout.Actions>
          </FormLayout.Content>
        )
      }}
    </Formik>
  )
}
